// Single source of runtime configuration for the app.
//
// Two layers, runtime winning over build time:
//
//   1. window._env_ — rendered into /env-config.js from the container's environment at
//      startup (see esb-admin/nginx/env-config.template.js and the entrypoint hook
//      docker/40-render-env-config.sh). This is what lets one built image serve every
//      site: change `environment:` in compose, restart, done — no rebuild.
//   2. process.env — the values CRA baked into the bundle at build time from the ARG
//      defaults in docker/docker-esb-admin. Used by `npm start` (which has no
//      env-config.js) and as the fallback when a variable is absent at runtime.
//
// The `baked` map below deliberately spells out every variable as a static
// `process.env.REACT_APP_*` property access. webpack's DefinePlugin substitutes those
// textually at build time, so a dynamic `process.env[key]` lookup would NOT be replaced
// and would silently read undefined in the production bundle.

const baked = {
    REACT_APP_DATASERVICE_URL: process.env.REACT_APP_DATASERVICE_URL,
    REACT_APP_INTERFACESERVICE_URL: process.env.REACT_APP_INTERFACESERVICE_URL,
    REACT_APP_RBAC_BASE_URL: process.env.REACT_APP_RBAC_BASE_URL,
    REACT_APP_RBAC_APP_ID: process.env.REACT_APP_RBAC_APP_ID,
    REACT_APP_RBAC_TENANT_NAME: process.env.REACT_APP_RBAC_TENANT_NAME,
    REACT_APP_MQTT_BROKER: process.env.REACT_APP_MQTT_BROKER,
    REACT_APP_MQTT_USERNAME: process.env.REACT_APP_MQTT_USERNAME,
    REACT_APP_MQTT_PASSWORD: process.env.REACT_APP_MQTT_PASSWORD,
    REACT_APP_MQTT_ALARM_TOPIC: process.env.REACT_APP_MQTT_ALARM_TOPIC,
    REACT_APP_LOCAL_DB_NAME: process.env.REACT_APP_LOCAL_DB_NAME,
    REACT_APP_LOCAL_DB_STORE: process.env.REACT_APP_LOCAL_DB_STORE,
    REACT_APP_ALARM_ROUTING: process.env.REACT_APP_ALARM_ROUTING,

    REACT_APP_AUTH_MODE: process.env.REACT_APP_AUTH_MODE,
    REACT_APP_SSO_ENABLED: process.env.REACT_APP_SSO_ENABLED,
    REACT_APP_SSO_CLIENTID: process.env.REACT_APP_SSO_CLIENTID,
    REACT_APP_SSO_TENANTID: process.env.REACT_APP_SSO_TENANTID,

    REACT_APP_AUTH_LAYOUT: process.env.REACT_APP_AUTH_LAYOUT,
    REACT_APP_DASHBOARD: process.env.REACT_APP_DASHBOARD,
    REACT_APP_THEME: process.env.REACT_APP_THEME,
};

const runtime = (typeof window !== 'undefined' && window._env_) || {};

// An empty string counts as absent, not as an intentional override: envsubst writes ''
// for any variable the template names but the environment does not set, so without this
// a variable omitted from compose would blank out its baked default rather than fall
// back to it. Returns undefined when neither layer has a value, matching what a bare
// process.env.X read did before, so call sites keep their existing semantics.
function pick(key) {
    const value = runtime[key];

    // Also ignore an unsubstituted placeholder. envsubst only replaces the variables named
    // in its SHELL-FORMAT argument, so a key present in the template but missing from that
    // list renders literally as "${REACT_APP_FOO}" — which is a value, not an absence, and
    // would otherwise be handed to the app as a URL or a flag.
    const usable =
        value !== undefined && value !== '' && !String(value).startsWith('${');

    return usable ? value : baked[key];
}

/** The three shapes alarm routing takes. Anything else is a typo, not a fourth mode. */
const ALARM_ROUTING_MODES = ['priority', 'type', 'group'];

/**
 * How this deployment routes alarms, as one setting rather than two booleans.
 *
 * This replaced REACT_APP_USE_DEFAULT_ALARM_ROUTES and a would-be REACT_APP_ALARM_DESTINATIONS.
 * Two flags gave four combinations, one of which - no destinations, yet alarm types assigning
 * groups - meant nothing, and every screen had to combine both and hope they agreed. The
 * choice was always three-way.
 *
 * An unrecognised value falls back to 'group' rather than failing: a console that will not
 * render is worse than one showing the arrangement every site already uses.
 */
function alarmRouting() {
    const mode = pick('REACT_APP_ALARM_ROUTING');
    return ALARM_ROUTING_MODES.includes(mode) ? mode : 'group';
}

/** The two the console draws. Anything else is a typo, not a third theme. */
const THEMES = ['dark', 'light'];

/**
 * Which palette this deployment draws in.
 *
 * Dark unless a site asks otherwise - it is what the console is designed in now, and light is
 * kept for a site that wants what it had. An unrecognised value falls back rather than failing,
 * the same way alarmRouting does.
 */
function theme() {
    const name = pick('REACT_APP_THEME');
    return THEMES.includes(name) ? name : 'dark';
}

export const config = {
    theme: theme(),

    dataServiceUrl: pick('REACT_APP_DATASERVICE_URL'),
    interfaceServiceUrl: pick('REACT_APP_INTERFACESERVICE_URL'),

    rbacBaseUrl: pick('REACT_APP_RBAC_BASE_URL'),
    rbacAppId: pick('REACT_APP_RBAC_APP_ID'),
    rbacTenantName: pick('REACT_APP_RBAC_TENANT_NAME'),

    mqttBroker: pick('REACT_APP_MQTT_BROKER'),
    mqttUsername: pick('REACT_APP_MQTT_USERNAME'),
    mqttPassword: pick('REACT_APP_MQTT_PASSWORD'),
    mqttAlarmTopic: pick('REACT_APP_MQTT_ALARM_TOPIC'),

    localDbName: pick('REACT_APP_LOCAL_DB_NAME'),
    localDbStore: pick('REACT_APP_LOCAL_DB_STORE'),

    // How alarms reach their destinations, and therefore which screens exist:
    //
    //   'priority'  destinations are chosen on the alarm priority - its Alerts/Emails/Viewer
    //               flags and their topics. The pre-route mechanism, and what MHAC uses. No
    //               Alarm Destinations section, no per-group screen, no User Role.
    //   'type'   destinations and their alarm groups are both set on the alarm type screen.
    //   'group'  destinations are set on the alarm type, then narrowed per group on the
    //            screen behind an alarm group row. What every site uses today.
    //
    // Defaults to 'group', which is what every deployment does now - so a site that sets
    // nothing keeps exactly what it has.
    alarmRouting: alarmRouting(),

    // Which authority a deployment uses for authentication and authorization.
    //
    //   'rbac'   sign in through the RBAC service, exchange its token at
    //            /auth/rbac-exchange, and evaluate permissions per user via useCan()
    //   'local'  sign in at /auth/login against the ESB user store, and evaluate
    //            permissions from the session's roles (see src/auth/urnRoles.js)
    //
    // Defaults to 'local' so a deployment that has not been given an RBAC tenant keeps
    // working rather than presenting a console nobody can sign in to.
    authMode: pick('REACT_APP_AUTH_MODE') === 'rbac' ? 'rbac' : 'local',

    // Entra SSO. In 'rbac' mode the client id and tenant are fetched from RBAC per app
    // (src/rbac/msal.js) and these are ignored; in 'local' mode they configure MSAL directly.
    ssoEnabled: pick('REACT_APP_SSO_ENABLED') === 'true',
    ssoClientId: pick('REACT_APP_SSO_CLIENTID'),
    ssoTenantId: pick('REACT_APP_SSO_TENANTID'),

    // Presentation of the sign-in and sign-out screens. 'card' is the GHD arrangement: a
    // centred card over the artwork instead of a full-height panel. The screens themselves
    // are the same either way -
    // only the frame differs, so this is a layout switch and not a second set of components.
    authLayout: pick('REACT_APP_AUTH_LAYOUT') === 'card' ? 'card' : 'split',

    // Which dashboard the admin landing route renders. 'ghd' is a sensor/occupancy view over
    // /api/v1/data/sensors/schedule; 'standard' is the alarm-oriented default. Unlike the
    // layout above this is a genuinely different screen, so it is a component variant.
    //
    // It selects what /user lands on, not what /admin does - the two viewers are alternatives
    // on the non-admin landing route (App.js), and this also decides which of the two rows the
    // Viewers menu offers (admin/menuItems.js). The admin dashboard is the same everywhere.
    dashboard: pick('REACT_APP_DASHBOARD') === 'ghd' ? 'ghd' : 'standard',
};

export default config;
